export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface CreditCard {
  id: string;
  name: string;
  issuer: string;
  network?: string;
  confidence?: 'high' | 'medium' | 'low' | 'none';
  score?: number;
}

// Enrollment request types
export interface TellerEnrollment {
  access_token: string;
}

export interface StripeEnrollment {
  provider: 'stripe';
  stripe_session_id: string;
}

export interface QuilttEnrollment {
  provider: 'quiltt';
  quiltt_connection_id: string;
  quiltt_provider: string;
}

// Enrollment status response types
export interface EnrollmentStatus {
  enrollment_id: string;
  status: 'pending' | 'connected' | 'business_accounts_syncd' | 'completed' | 'expired' | 'error';
  provider: string;
  connected_at?: string;
  accounts_count: number;
  credit_cards_count: number;
  last_sync_at: string;
  sync_progress: number;
  ready_for_card_selection: boolean;
  final_state: boolean;
}

export interface SimulationEnrollmentsResponse {
  enrollments: EnrollmentStatus[];
  all_ready_for_analysis: boolean;
}

// Raw API response types (strings from backend)
export interface SimulationResultsApiResponse {
  simulation: {
    id: string;
    status: string;
    start_date: string;
    end_date: string;
    results: {
      actually_earned: {
        total_dollars: string;
        description: string;
      };
      wallet_potential: {
        total_dollars: string;
        description: string;
        vs_actual: string;
      };
      global_potential: {
        total_dollars: string;
        description: string;
        vs_actual: string;
        vs_wallet: string;
      };
      total_spend: string;
      optimization_score: string;
      missed_opportunity: string;
      actual_total: string;
      optimized_total: string;
      improvement_percentage: string;
      missed_rewards_amount: string;
    };
    recommendations: RecommendationApiResponse[];
    uncategorized_summary: {
      transaction_count: number;
      categorized_count: number;
      uncategorized_count: number;
      uncategorized_amount: string;
      uncategorized_percentage: number | null;
    };
  };
}

export interface RecommendationApiResponse {
  type: string;
  priority: 'high' | 'medium' | 'low';
  message: string;
  cards?: CreditCard[];
}

// Converted types (numbers for frontend use)
export interface SimulationResults {
  id: string;
  status: string;
  start_date: string;
  end_date: string;
  results: {
    actually_earned: {
      total_dollars: number;
      description: string;
    };
    wallet_potential: {
      total_dollars: number;
      description: string;
      vs_actual: number;
    };
    global_potential: {
      total_dollars: number;
      description: string;
      vs_actual: number;
      vs_wallet: number;
    };
    total_spend: number;
    optimization_score: number;
    missed_opportunity: number;
    actual_total: number;
    optimized_total: number;
    improvement_percentage: number;
    missed_rewards_amount: number;
  };
  recommendations: Recommendation[];
  uncategorized_summary: {
    transaction_count: number;
    categorized_count: number;
    uncategorized_count: number;
    uncategorized_amount: number;
    uncategorized_percentage: number | null;
  };
}

export interface Recommendation {
  type: string;
  priority: 'high' | 'medium' | 'low';
  message: string;
  cards?: CreditCard[];
}

// Helper function to convert string numbers from API to actual numbers
function convertSimulationResponse(apiResponse: SimulationResultsApiResponse): SimulationResults {
  const parseFloat = (value: string): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  return {
    id: apiResponse.simulation.id,
    status: apiResponse.simulation.status,
    start_date: apiResponse.simulation.start_date,
    end_date: apiResponse.simulation.end_date,
    results: {
      actually_earned: {
        total_dollars: parseFloat(apiResponse.simulation.results.actually_earned.total_dollars),
        description: apiResponse.simulation.results.actually_earned.description,
      },
      wallet_potential: {
        total_dollars: parseFloat(apiResponse.simulation.results.wallet_potential.total_dollars),
        description: apiResponse.simulation.results.wallet_potential.description,
        vs_actual: parseFloat(apiResponse.simulation.results.wallet_potential.vs_actual),
      },
      global_potential: {
        total_dollars: parseFloat(apiResponse.simulation.results.global_potential.total_dollars),
        description: apiResponse.simulation.results.global_potential.description,
        vs_actual: parseFloat(apiResponse.simulation.results.global_potential.vs_actual),
        vs_wallet: parseFloat(apiResponse.simulation.results.global_potential.vs_wallet),
      },
      total_spend: parseFloat(apiResponse.simulation.results.total_spend),
      optimization_score: parseFloat(apiResponse.simulation.results.optimization_score),
      missed_opportunity: parseFloat(apiResponse.simulation.results.missed_opportunity),
      actual_total: parseFloat(apiResponse.simulation.results.actual_total),
      optimized_total: parseFloat(apiResponse.simulation.results.optimized_total),
      improvement_percentage: parseFloat(apiResponse.simulation.results.improvement_percentage),
      missed_rewards_amount: parseFloat(apiResponse.simulation.results.missed_rewards_amount),
    },
    recommendations: apiResponse.simulation.recommendations.map(rec => ({
      type: rec.type,
      priority: rec.priority,
      message: rec.message,
      cards: rec.cards,
    })),
    uncategorized_summary: {
      transaction_count: apiResponse.simulation.uncategorized_summary.transaction_count,
      categorized_count: apiResponse.simulation.uncategorized_summary.categorized_count,
      uncategorized_count: apiResponse.simulation.uncategorized_summary.uncategorized_count,
      uncategorized_amount: parseFloat(apiResponse.simulation.uncategorized_summary.uncategorized_amount),
      uncategorized_percentage: apiResponse.simulation.uncategorized_summary.uncategorized_percentage,
    },
  };
}

export interface ConnectedAccount {
  id: string;
  name: string;
  last_four: string;
  institution: string;
  type: string;
  mapping_status?: string;
  enrollment_id?: string;
  credit_card?: CreditCard;
  potential_cards?: CreditCard[];
  ignored?: boolean;
}

// Constants for localStorage
const AUTH_TOKEN_KEY = 'stashr_auth_token';
const REFRESH_TOKEN_KEY = 'stashr_refresh_token';
const TOKEN_EXPIRY_KEY = 'stashr_token_expiry';

export class ApiClient {
  private apiUrl: string;
  private authTokens: AuthTokens | null = null;
  private onUnauthorized?: () => void;

  constructor(apiUrl: string, onUnauthorized?: () => void) {
    this.apiUrl = apiUrl;
    this.onUnauthorized = onUnauthorized;
  }

  // Set the callback for unauthorized responses
  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  // Get current auth tokens
  getAuthTokens(): AuthTokens | null {
    return this.authTokens;
  }

  // Set auth tokens in memory and localStorage
  setAuthTokens(tokens: AuthTokens) {
    this.authTokens = tokens;
    localStorage.setItem(AUTH_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, tokens.expiresAt.toString());
  }

  // Clear auth tokens from memory and localStorage
  clearAuthTokens() {
    this.authTokens = null;
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  // Load tokens from localStorage and validate them
  async loadTokensFromStorage(): Promise<boolean> {
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiryString = localStorage.getItem(TOKEN_EXPIRY_KEY);
    const expiresAt = expiryString ? parseInt(expiryString, 10) : 0;

    // Check if we have valid tokens that haven't expired
    if (accessToken && refreshToken && expiresAt > Date.now()) {
      // Test if the access token is still valid by making a simple API call
      try {
        const testResponse = await fetch(`${this.apiUrl}/api/bank_account_mappings`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        // If we get a 401, the token is invalid - clear storage
        if (testResponse.status === 401) {
          console.log('Access token is invalid, clearing stored tokens');
          this.clearAuthTokens();
          return false;
        }

        // Token is valid, set auth tokens
        this.authTokens = {
          accessToken,
          refreshToken,
          expiresAt
        };

        return true;
      } catch (error) {
        console.error('Error testing token validity:', error);
        // On network error, assume tokens might be valid and proceed
        this.authTokens = {
          accessToken,
          refreshToken,
          expiresAt
        };
        return true;
      }
    }

    return false;
  }

  // Make authenticated API request with automatic 401 handling
  private async makeRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add Authorization header if we have tokens
    if (this.authTokens?.accessToken) {
      headers['Authorization'] = `Bearer ${this.authTokens.accessToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 responses
    if (response.status === 401) {
      console.log('Received 401, clearing tokens and triggering app reset');
      this.clearAuthTokens();
      if (this.onUnauthorized) {
        this.onUnauthorized();
      }
    }

    return response;
  }

  // Create anonymous user and get auth tokens
  async createAnonymousUser(source: string = 'simulation'): Promise<AuthTokens | null> {
    try {
      const response = await fetch(`${this.apiUrl}/api/sessions/anonymous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source })
      });

      if (!response.ok) {
        throw new Error('Failed to create anonymous user');
      }

      const data = await response.json();

      // Set expiry to 30 days from now
      const expiresAt = Date.now() + (30 * 24 * 60 * 60 * 1000);

      const tokens: AuthTokens = {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt
      };

      this.setAuthTokens(tokens);
      return tokens;
    } catch (error) {
      console.error('Error creating anonymous user:', error);
      return null;
    }
  }

  // Create simulation
  async createSimulation(): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Failed to create simulation');
    }

    return response.json();
  }

  // Get simulation details
  async getSimulation(simulationId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}`);

    if (!response.ok) {
      throw new Error('Failed to get simulation');
    }

    return response.json();
  }

  // Run simulation
  async runSimulation(simulationId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/run`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Failed to run simulation');
    }

    return response.json();
  }

  // Create enrollment within a simulation
  async createEnrollment(
    simulationId: string, 
    enrollmentData: TellerEnrollment | StripeEnrollment | QuilttEnrollment
  ): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/enrollments`, {
      method: 'POST',
      body: JSON.stringify(enrollmentData)
    });

    if (!response.ok) {
      throw new Error('Failed to create enrollment');
    }

    return response.json();
  }

  // Get enrollments for a simulation
  async getSimulationEnrollments(simulationId: string): Promise<SimulationEnrollmentsResponse> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/enrollments`);

    if (!response.ok) {
      throw new Error('Failed to get simulation enrollments');
    }

    return response.json();
  }

  // Get enrollment status
  async getEnrollmentStatus(enrollmentId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/enrollments/${enrollmentId}`);

    if (!response.ok) {
      throw new Error('Failed to check enrollment status');
    }

    return response.json();
  }

  // Get active bank provider
  async getActiveBankProvider(): Promise<'teller' | 'stripe' | 'quiltt'> {
    try {
      const response = await this.makeRequest(`${this.apiUrl}/api/system/active_bank_provider`);

      if (!response.ok) {
        console.warn('Failed to get active bank provider, defaulting to stripe');
        return 'stripe';
      }

      const data = await response.json();
      return data.provider || 'stripe';
    } catch (error) {
      console.warn('Error getting active bank provider, defaulting to stripe:', error);
      return 'stripe';
    }
  }

  // Create Stripe Financial Connections session
  async createStripeSession(simulationId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/stripe_session`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Failed to create Stripe session');
    }

    return response.json();
  }

  // Create Quiltt session
  async createQuilttSession(simulationId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/quiltt_session`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Failed to create Quiltt session');
    }

    return response.json();
  }

  // Create Plaid Link session
  async createPlaidSession(simulationId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/plaid_session`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Failed to create Plaid session');
    }

    return response.json();
  }

  // Exchange Plaid public token for access token
  async exchangePlaidToken(simulationId: string, data: { public_token: string; metadata: any }): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/plaid_exchange`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error('Failed to exchange Plaid token');
    }

    return response.json();
  }


  // Get bank account mappings
  async getBankAccountMappings(enrollmentId?: string): Promise<any> {
    const url = enrollmentId
      ? `${this.apiUrl}/api/bank_account_mappings?enrollment_id=${enrollmentId}`
      : `${this.apiUrl}/api/bank_account_mappings`;

    const response = await this.makeRequest(url);

    if (!response.ok) {
      // Don't throw for 404s, user might not have any enrollments yet
      if (response.status === 404) {
        return { accounts: [] };
      }
      throw new Error('Failed to fetch bank account mappings');
    }

    return response.json();
  }

  // Get unmapped accounts
  async getUnmappedAccounts(enrollmentId: string): Promise<any> {
    const response = await this.makeRequest(
      `${this.apiUrl}/api/bank_account_mappings/unmapped?enrollment_id=${enrollmentId}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch unmapped accounts');
    }

    return response.json();
  }

  // Update bank account mapping
  async updateBankAccountMapping(accountId: string, creditCardId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/bank_account_mappings/${accountId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        bank_account: {
          credit_card_id: creditCardId
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update bank account mapping');
    }

    return response.json();
  }

  // Ignore bank account
  async ignoreBankAccount(accountId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/bank_account_mappings/${accountId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        bank_account: {
          ignored: true
        }
      })
    });

    if (!response.ok) {
      throw new Error('Failed to ignore bank account');
    }

    return response.json();
  }

  // Submit missing credit card
  async submitMissingCreditCard(creditCardName: string, creditCardUrl?: string, enrollmentId?: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/credit_cards/missing_credit_cards`, {
      method: 'POST',
      body: JSON.stringify({
        missing_credit_card: {
          credit_card_name: creditCardName,
          credit_card_url: creditCardUrl || '',
          enrollment_id: enrollmentId
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit missing credit card');
    }

    return response.json();
  }

  // Get simulation results
  async getSimulationResults(simulationId: string): Promise<{ simulation: SimulationResults }> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulation_results/${simulationId}`);

    if (!response.ok) {
      throw new Error('Failed to fetch simulation results');
    }

    const apiResponse: SimulationResultsApiResponse = await response.json();
    const convertedResults = convertSimulationResponse(apiResponse);
    
    return { simulation: convertedResults };
  }

  // Share simulation and get public URL
  async shareSimulation(simulationId: string): Promise<{ share_url: string; public_id: string }> {
    const response = await this.makeRequest(`${this.apiUrl}/api/simulations/${simulationId}/share`, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Failed to share simulation');
    }

    return response.json();
  }

  // Get public share data (no auth required)
  async getPublicShare(publicId: string): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/public_shares/${publicId}`, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch public share');
    }

    return response.json();
  }

  // Delete all user data
  async deleteUserData(): Promise<void> {
    const response = await this.makeRequest(`${this.apiUrl}/api/users/delete_data`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error('Failed to delete user data');
    }
  }

  // Get all enrollments for the current user
  async getAllEnrollments(): Promise<any> {
    const response = await this.makeRequest(`${this.apiUrl}/api/enrollments`);

    if (!response.ok) {
      throw new Error('Failed to fetch enrollments');
    }

    return response.json();
  }
}
